import { Trans, useLingui } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';
import type { ReactNode } from '@tanstack/react-router';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useCurrentMember, useLeaveSubscription } from 'src/api/queries';
import { Button, Modal, NotificationBox } from 'src/components';

export const LeaveSubscriptionModal = ({ trigger }: { trigger: ReactNode }) => {
  const { t } = useLingui();
  const leaveSubscription = useLeaveSubscription();

  const currentMember = useCurrentMember();

  return (
    <Modal.Root>
      <Modal.Trigger asChild>{trigger}</Modal.Trigger>
      <Modal.Content title={t`Leave subscription`}>
        <Typography variant="body-md" className="mb-24">
          <Trans>Are you sure you want to leave this subscription?</Trans>
        </Typography>

        <Typography variant="body-md">
          <Trans>
            You&apos;ll lose access to all subscription features and benefits.
          </Trans>
        </Typography>

        {leaveSubscription.isError && (
          <NotificationBox error={leaveSubscription.error} className="my-24">
            <Trans>Try again or contact support if the problem persists.</Trans>
          </NotificationBox>
        )}

        <div className="mt-24 flex justify-center gap-16">
          <Button
            variant="secondary"
            loading={leaveSubscription.isPending}
            onClick={() => {
              if (!currentMember.data?.id) return;

              leaveSubscription.mutate({
                subscriptionRoleId: currentMember.data?.id,
              });
            }}
            disabled={!currentMember.data?.seat?.id}
            analytics={{
              text: 'Leave subscription',
              location: ComponentLocations.MANAGE_CONNECT.MAIN,
              action: 'leave subscription',
            }}
          >
            <Trans>Leave subscription</Trans>
          </Button>

          <Modal.Close>
            <Button
              analytics={{
                text: 'Stay',
                location: ComponentLocations.MANAGE_CONNECT.MAIN,
                action: 'stay (close modal)',
              }}
            >
              <Trans>Stay</Trans>
            </Button>
          </Modal.Close>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};
